import { getNiceTickValues } from "recharts-scale";

/**
 * Recharts' own "nice" domain snaps to the smallest round number >= the data
 * max, so a max of exactly 100 gives no headroom above the tallest bar. Force
 * one extra tick step whenever the data touches the top tick, and return the
 * full tick array so Recharts doesn't have to re-derive ticks from a fixed
 * domain itself (its tickCount-based derivation can produce uneven steps,
 * e.g. [0, 35, 70, 125] instead of [0, 25, 50, 75, 100, 125]).
 *
 * `step`, when provided, skips the tickCount-based "nice" step derivation
 * (which can land on coarse steps like 20/25) in favour of a fixed increment
 * -- e.g. step 5 for a 0-100% axis.
 */
export const getNiceAxisTicks = (
  values: number[],
  tickCount = 5,
  step?: number
) => {
  const rawMax = Math.max(0, ...values);

  if (step) {
    let lastTick = Math.ceil(rawMax / step) * step;
    if (lastTick <= rawMax) lastTick += step;
    const ticks: number[] = [];
    for (let tick = 0; tick <= lastTick; tick += step) ticks.push(tick);
    return ticks;
  }

  const niceTicks: number[] = getNiceTickValues([0, rawMax], tickCount, false);
  const niceStep = niceTicks.length > 1 ? niceTicks[1] - niceTicks[0] : 1;
  const lastTick = niceTicks[niceTicks.length - 1];
  return lastTick > rawMax ? niceTicks : [...niceTicks, lastTick + niceStep];
};
